/**
 * Builds the touch-aware setters that CampaignForm threads into its sections.
 *
 * Each returned setter calls `markTouched(field)` before delegating to the
 * underlying form setter, so any user edit flips the corresponding field's
 * touched flag and surfaces inline validation.
 *
 * Number-typed fields (the four NCEMI funding shares + their consumers that
 * accept strings) are adapted via `parseOptionalNumber` so the section
 * components can keep their `string` input contracts.
 */

import { useCallback, useMemo } from 'react';
import { UseCampaignFormResult } from 'src/hooks/useCampaignForm';
import { FieldKey } from 'src/utils/valueMatching/form/formValidationUtils';
import { parseOptionalNumber } from 'src/utils/helpers';

/** Wraps a setter so that calling it marks the field as touched. */
function useTouchWrapped<T>(
    setter: (value: T) => void,
    key: FieldKey,
    markTouched?: (field: FieldKey) => void,
): (value: T) => void {
    return useMemo(
        () => (value: T) => {
            markTouched?.(key);
            setter(value);
        },
        [setter, key, markTouched],
    );
}

export interface TouchedSetters {
    setTitle: (value: string) => void;
    setAsins: (value: string) => void;
    setStartDate: (value: string) => void;
    setStartTime: (value: string) => void;
    setEndDate: (value: string) => void;
    setEndTime: (value: string) => void;
    setSellerShareStr: (value: string) => void;
    setBrandShareStr: (value: string) => void;
    setBankShareStr: (value: string) => void;
    setAmazonShareStr: (value: string) => void;
    setBrandName: (value: string) => void;
    setPriceLeadingGuardrail: (value: string) => void;
    setTermsAndConditions: (value: string) => void;
    setCashbackExtentGuardrail: (value: string) => void;
    setCashbackPercentLimitGuardrail: (value: string) => void;
    setCashbackAbsoluteLimitGuardrail: (value: string) => void;
    setBuybackPercentOfAsinPrice: (value: string) => void;
    setBuybackPercentOfBuybackOffer: (value: string) => void;
    setBuybackAbsoluteValueCap: (value: string) => void;
    setDealPercentOfOriginalDealPrice: (value: string) => void;
}

export function useTouchedSetters(
    form: UseCampaignFormResult,
    markTouched?: (field: FieldKey) => void,
): TouchedSetters {
    const setSellerShareNum = useCallback(
        (value: string) => form.setSellerShare(parseOptionalNumber(value)),
        [form.setSellerShare],
    );
    const setBrandShareNum = useCallback(
        (value: string) => form.setBrandShare(parseOptionalNumber(value)),
        [form.setBrandShare],
    );
    const setBankShareNum = useCallback(
        (value: string) => form.setBankShare(parseOptionalNumber(value)),
        [form.setBankShare],
    );
    const setAmazonShareNum = useCallback(
        (value: string) => form.setAmazonShare(parseOptionalNumber(value)),
        [form.setAmazonShare],
    );

    return {
        setTitle: useTouchWrapped(form.setTitle, 'title', markTouched),
        setAsins: useTouchWrapped(form.setAsins, 'asins', markTouched),
        setStartDate: useTouchWrapped(form.setStartDate, 'startDateTime', markTouched),
        setStartTime: useTouchWrapped(form.setStartTime, 'startDateTime', markTouched),
        setEndDate: useTouchWrapped(form.setEndDate, 'endDateTime', markTouched),
        setEndTime: useTouchWrapped(form.setEndTime, 'endDateTime', markTouched),
        setSellerShareStr: useTouchWrapped(setSellerShareNum, 'sellerShare', markTouched),
        setBrandShareStr: useTouchWrapped(setBrandShareNum, 'brandShare', markTouched),
        setBankShareStr: useTouchWrapped(setBankShareNum, 'bankShare', markTouched),
        setAmazonShareStr: useTouchWrapped(setAmazonShareNum, 'amazonShare', markTouched),
        setBrandName: useTouchWrapped(form.setBrandName, 'brandName', markTouched),
        setPriceLeadingGuardrail: useTouchWrapped(
            form.setPriceLeadingGuardrail,
            'priceLeadingGuardrail',
            markTouched,
        ),
        setTermsAndConditions: useTouchWrapped(
            form.setTermsAndConditions,
            'termsAndConditions',
            markTouched,
        ),
        setCashbackExtentGuardrail: useTouchWrapped(
            form.setCashbackExtentGuardrail,
            'cashbackExtentGuardrail',
            markTouched,
        ),
        setCashbackPercentLimitGuardrail: useTouchWrapped(
            form.setCashbackPercentLimitGuardrail,
            'cashbackPercentLimitGuardrail',
            markTouched,
        ),
        setCashbackAbsoluteLimitGuardrail: useTouchWrapped(
            form.setCashbackAbsoluteLimitGuardrail,
            // eslint-disable-next-line no-secrets/no-secrets
            'cashbackAbsoluteLimitGuardrail',
            markTouched,
        ),
        setBuybackPercentOfAsinPrice: useTouchWrapped(
            form.setBuybackPercentOfAsinPrice,
            'buybackPercentOfAsinPrice',
            markTouched,
        ),
        setBuybackPercentOfBuybackOffer: useTouchWrapped(
            form.setBuybackPercentOfBuybackOffer,
            'buybackPercentOfBuybackOffer',
            markTouched,
        ),
        setBuybackAbsoluteValueCap: useTouchWrapped(
            form.setBuybackAbsoluteValueCap,
            'buybackAbsoluteValueCap',
            markTouched,
        ),
        setDealPercentOfOriginalDealPrice: useTouchWrapped(
            form.setDealPercentOfOriginalDealPrice,
            'dealPercentOfOriginalDealPrice',
            markTouched,
        ),
    };
}
